import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useCallback, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { usePhoneOptionsContext } from '../../../../../../context/PhoneOptionsContext';
import useAxiosHook from '../../../../../../hooks/useAxiosHook';
import { WithData } from '../../../../../../types/api';
import {
  MunicipalityCollection,
  MunicipalityPlaceCollection,
} from '../../../../../../types/api/municipalities';
import { PlacesStreetCollection } from '../../../../../../types/api/places';
import { isStreetFreeInputAllowed } from '../../../../../../utils/constants/misc';
import { invalidPhoneNumberCharactersRegex } from '../../../../../../utils/constants/phoneNumbers';
import {
  formatMobileNumber,
  getPhoneorMobileNumberRegion,
} from '../../../../../../utils/helpers/phoneNumbers';
import FieldWithErrors from '../../../../../Forms/ReactHookForm/FieldWithErrors';

type Props = {
  clientData: any;
  isEditDialog: boolean;
};

function GeneralInfoTab({ clientData, isEditDialog }: Props) {
  const { t } = useTranslation();
  const { getValues, setValue } = useFormContext();

  const municipalityId = useWatch({ name: 'address.municipalityId' });
  const placeId = useWatch({ name: 'address.placeId' });
  const streetId = useWatch({ name: 'address.streetId' });

  const { countryOptionTemplate, languages, selectedCountryTemplate } =
    usePhoneOptionsContext();

  const { data: municipalities, isLoading: isMunicipalitiesLoading } =
    useAxiosHook<WithData<MunicipalityCollection>>('/municipalities?limit=0');

  const {
    data: places,
    reload: reloadPlaces,
    isLoading: isPlacesLoading,
  } = useAxiosHook<MunicipalityPlaceCollection>(
    `/municipalities/${municipalityId}/places?limit=0`,
    {
      skipWhen: !municipalityId,
      skipCache: true,
    }
  );

  const {
    data: streets,
    reload: reloadStreets,
    isLoading: isStreetsLoading,
  } = useAxiosHook<WithData<PlacesStreetCollection>>(
    `/places/${placeId}/streets?limit=0`,
    {
      skipWhen: !placeId,
      skipCache: true,
    }
  );

  const { data: occupations, isLoading: isOccupationsLoading } =
    useAxiosHook<any>('/occupations');

  const occupationOptions = useMemo(
    () =>
      occupations && occupations.length > 0
        ? occupations?.map((m: any) => ({
            label: m.ime,
            value: m.id,
          }))
        : [],
    [occupations]
  );

  const municipalitiesOptions = useMemo(
    () =>
      municipalities && municipalities.data.length > 0
        ? municipalities?.data?.map((m) => ({
            label: m.name,
            value: m.id,
            city_id: m.city_id,
          }))
        : [],
    [municipalities, municipalityId]
  );

  const placesOptions = useMemo(
    () =>
      places && places.length > 0
        ? places?.map((m) => ({
            label: m.place_name,
            value: m.place_id,
            postal_code: m.zip,
          }))
        : [],
    [places, municipalityId]
  );

  const streetsOptions = useMemo(
    () =>
      streets && streets?.data?.length > 0
        ? streets?.data?.map((m) => ({ label: m.name, value: m.id }))
        : [],
    [streets, placeId]
  );

  const clientTypeOptions = useMemo(
    () => [
      { label: t('Individual entity'), value: false },
      { label: t('Legal entity'), value: true },
    ],
    [t]
  );

  const onChangeClientType = useCallback(
    (e) => {
      const { name, value } = e.target;
      // set direkcija_id to client_id for showing default this client is headquarter
      // and for showing Charging Departments tab
      // set departments default for edit if there  Registering customer
      setValue(name, value);
      setValue(
        'direkcija_id',
        value
          ? isEditDialog
            ? clientData?.id
            : getValues()?.direkcija_id
          : null
      );
      setValue(
        'maticen_broj',
        value
          ? isEditDialog
            ? clientData?.maticen_broj
            : getValues()?.maticen_broj
          : ''
      );
      setValue(
        'danocen_broj',
        value
          ? isEditDialog
            ? clientData?.danocen_broj
            : getValues()?.danocen_broj
          : ''
      );
      setValue(
        'direkcija_ime',
        value
          ? isEditDialog
            ? t('This client is a headquarter')
            : t('This client is a headquarter')
          : null
      );
      setValue(
        'departments',
        value && isEditDialog && clientData?.departments?.length > 0
          ? clientData.departments
          : [
              {
                id: -1,
                name: 'Default',
                headquarters_id: clientData.id ?? 0,
                charge_customer_id: 0,
              },
            ]
      );
    },
    [setValue, clientData, getValues, isEditDialog, t]
  );
  console.log('values ', getValues());
  return (
    <div className="general-info-tab p-fluid">
      <div className="p-grid">
        <div className="p-col-12 p-md-6">
          <FieldWithErrors name="pravno_lice" label={t('Type')}>
            <Controller
              name="pravno_lice"
              render={({ field }) => (
                <Dropdown
                  id="pravno_lice"
                  name="pravno_lice"
                  options={clientTypeOptions}
                  value={field.value}
                  onChange={onChangeClientType}
                />
              )}
            />
          </FieldWithErrors>

          <FieldWithErrors name="ime" label={t('Name')}>
            <Controller
              name="ime"
              render={({ field }) => (
                <InputText
                  id="ime"
                  name="ime"
                  value={field.value}
                  onChange={field.onChange}
                  max={210}
                />
              )}
            />
          </FieldWithErrors>

          <FieldWithErrors
            name="address.municipalityId"
            label={t('Municipality')}
          >
            <Controller
              name="address.municipalityId"
              render={({ field }) => (
                <Dropdown
                  id="address.municipalityId"
                  name="address.municipalityId"
                  options={municipalitiesOptions}
                  value={field.value}
                  onChange={(e) => {
                    if (e.value) {
                      field.onChange(e.value);
                      const item: any = municipalitiesOptions?.find(
                        (s) => s.value === e.value
                      );
                      console.log('item', item);
                      setValue('address.municipalityName', item?.label);
                      setValue('address.cityId', item?.city_id);

                      reloadPlaces({
                        url: `municipalities/${e.value}/places?limit=0`,
                      });

                      setValue('address.placeId', null);
                      setValue('address.placeName', '');
                      setValue('address.streetId', null);
                      setValue('address.streetName', '');
                      setValue('address.housenumber', '');
                      setValue('address.entrance', '');
                      setValue('address.apartment', '');
                    }
                  }}
                  filter
                  filterPlaceholder={t('Search')}
                  placeholder={isMunicipalitiesLoading ? t('Loading...') : ''}
                  className="data-cy-municipalityId"
                />
              )}
            />
          </FieldWithErrors>

          <FieldWithErrors name="address.placeId" label={t('Place')}>
            <Controller
              name="address.placeId"
              render={({ field }) => (
                <Dropdown
                  id="address.placeId"
                  name="address.placeId"
                  options={placesOptions}
                  value={field.value}
                  onChange={(e) => {
                    console.log('e.value', e);
                    if (e.value) {
                      field.onChange(e.value);
                      const item = placesOptions?.find(
                        (s) => s.value === e.value
                      );
                      setValue('address.placeName', item?.label);
                      setValue('address.postcode', item?.postal_code);

                      reloadStreets({
                        url: `places/${e.value}/streets?limit=0`,
                      });
                    } else {
                      setValue('address.placeId', null);
                      setValue('address.placeName', '');
                      setValue('address.streetId', null);
                      setValue('address.streetName', '');
                      setValue('address.housenumber', '');
                      setValue('address.entrance', '');
                      setValue('address.apartment', '');
                    }
                  }}
                  filter
                  filterPlaceholder={t('Search')}
                  placeholder={isPlacesLoading ? t('Loading...') : ''}
                  disabled={!municipalityId || isPlacesLoading}
                  className="data-cy-placeId"
                />
              )}
            />
          </FieldWithErrors>

          {isStreetFreeInputAllowed ? (
            <FieldWithErrors name="address.rawName" label={t('Address')}>
              <Controller
                name="address.rawName"
                render={({ field }) => (
                  <InputText
                    id="address.rawName"
                    name="address.rawName"
                    value={field.value}
                    onChange={field.onChange}
                    disabled={!municipalityId || !placeId}
                    max={256}
                  />
                )}
              />
            </FieldWithErrors>
          ) : (
            <>
              <FieldWithErrors name="address.streetId" label={t('Street')}>
                <Controller
                  name="address.streetId"
                  render={({ field }) => (
                    <Dropdown
                      id="address.streetId"
                      name="address.streetId"
                      options={streetsOptions}
                      value={field.value}
                      onChange={(e) => {
                        console.log('eeeee ', e);
                        if (e.value) {
                          field.onChange(e.value);
                          const item = streetsOptions?.find(
                            (s) => s.value === e.value
                          );
                          setValue('address.streetName', item?.label);
                        } else {
                          setValue('address.streetId', null);
                          setValue('address.streetName', '');
                          setValue('address.housenumber', '');
                          setValue('address.entrance', '');
                          setValue('address.apartment', '');
                        }
                      }}
                      filter
                      filterPlaceholder={t('Search')}
                      placeholder={isStreetsLoading ? t('Loading...') : ''}
                      disabled={!placeId || isStreetsLoading}
                      className="data-cy-streetId"
                    />
                  )}
                />
              </FieldWithErrors>

              <div className="street-numbers">
                <FieldWithErrors
                  name="address.housenumber"
                  label={t('Street No.')}
                >
                  <Controller
                    name="address.housenumber"
                    render={({ field }) => (
                      <InputText
                        id="address.housenumber"
                        name="address.housenumber"
                        value={field.value}
                        onChange={field.onChange}
                        disabled={!streetId}
                      />
                    )}
                  />
                </FieldWithErrors>

                <span className="divider">/</span>

                <FieldWithErrors
                  name="address.entrance"
                  label={t('Entrance No.')}
                >
                  <Controller
                    name="address.entrance"
                    render={({ field }) => (
                      <InputText
                        id="address.entrance"
                        name="address.entrance"
                        value={field.value}
                        onChange={field.onChange}
                        disabled={!streetId}
                      />
                    )}
                  />
                </FieldWithErrors>

                <span className="divider">/</span>

                <FieldWithErrors name="address.apartment" label={t('Flat No.')}>
                  <Controller
                    name="address.apartment"
                    render={({ field }) => (
                      <InputText
                        id="address.apartment"
                        name="address.apartment"
                        value={field.value}
                        onChange={field.onChange}
                        disabled={!streetId}
                      />
                    )}
                  />
                </FieldWithErrors>
              </div>
            </>
          )}

          <FieldWithErrors name="dejnost_id" label={t('Occupation')}>
            <Controller
              name="dejnost_id"
              render={({ field }) => (
                <Dropdown
                  id="dejnost_id"
                  name="dejnost_id"
                  filter
                  options={occupationOptions}
                  value={field.value}
                  onChange={field.onChange}
                  // placeholder={isOccupationsLoading ? t('Loading...') : null}
                  disabled={isOccupationsLoading}
                />
              )}
            />
          </FieldWithErrors>

          <FieldWithErrors name="referenca" label={t('Reference No.')}>
            <Controller
              name="referenca"
              render={({ field }) => (
                <InputText
                  id="referenca"
                  name="referenca"
                  value={field.value}
                  onChange={field.onChange}
                  max={45}
                />
              )}
            />
          </FieldWithErrors>

          <FieldWithErrors name="komentar" label={t('Comment')}>
            <Controller
              name="komentar"
              render={({ field }) => (
                <InputText
                  id="komentar"
                  name="komentar"
                  value={field.value}
                  onChange={field.onChange}
                  max={500}
                />
              )}
            />
          </FieldWithErrors>

          <FieldWithErrors
            name="interen_klient"
            label={false}
            className="p-d-flex p-mb-2 p-jc-between"
          >
            <Controller
              name="interen_klient"
              render={({ field }) => (
                <>
                  <Checkbox
                    id="interen_klient"
                    name="interen_klient"
                    value={field.value}
                    checked={field.value}
                    onChange={field.onChange}
                  />
                  <label htmlFor="interen_klient" data-cy="interen_klient">
                    {t('This client is created for internal purposes')}
                  </label>
                </>
              )}
            />
          </FieldWithErrors>
        </div>

        <div className="p-col-12 p-md-6">
          <span className="dialog-section-divider">{t('Contact Info')}</span>
          <FieldWithErrors name="mobilen" label={t('Mobile')}>
            <div className="p-inputgroup number-group">
              <Controller
                name="mobilen_region"
                render={({ field }) => (
                  <Dropdown
                    style={{ padding: 0, width: '5rem' }}
                    id="mobilen_region"
                    options={languages}
                    itemTemplate={countryOptionTemplate}
                    valueTemplate={selectedCountryTemplate}
                    data-cy="mobilen_region"
                    {...field}
                  />
                )}
              />
              <Controller
                name="mobilen"
                render={({ field }) => (
                  <InputText
                    name="mobilen"
                    style={{ width: '100%' }}
                    value={formatMobileNumber(
                      getValues()?.mobilen,
                      getValues()?.mobilen_region ||
                        getPhoneorMobileNumberRegion(getValues()?.mobilen)
                    )}
                    onChange={(e) => {
                      field.onChange(
                        e.target.value?.replace(
                          invalidPhoneNumberCharactersRegex,
                          ''
                        )
                      );
                    }}
                    //       onChange={(e) => {
                    //   setValue('mobilen', e.target.value);
                    // }}
                    className="data-cy-mobile"
                  />
                )}
              />
            </div>
          </FieldWithErrors>

          <FieldWithErrors name="telefon" label={t('Phone')}>
            <div className="p-inputgroup">
              <Controller
                name="telefon_region"
                render={({ field }) => (
                  <Dropdown
                    style={{ padding: 0, width: '5rem' }}
                    id="telefon_region"
                    options={languages}
                    itemTemplate={countryOptionTemplate}
                    valueTemplate={selectedCountryTemplate}
                    data-cy="telefon_region"
                    {...field}
                  />
                )}
              />
              <Controller
                name="telefon"
                render={({ field }) => (
                  <InputText
                    name="telefon"
                    style={{ width: '100%' }}
                    value={formatMobileNumber(
                      getValues()?.telefon,
                      getValues()?.telefon_region ||
                        getPhoneorMobileNumberRegion(getValues()?.telefon)
                    )}
                    onChange={(e) => {
                      field.onChange(
                        e.target.value?.replace(
                          invalidPhoneNumberCharactersRegex,
                          ''
                        )
                      );
                    }}
                    //       onChange={(e) => {
                    //   setValue('mobilen', e.target.value);
                    // }}
                    className="data-cy-telefon"
                  />
                )}
              />
            </div>
          </FieldWithErrors>

          <FieldWithErrors name="faks" label={t('Fax')}>
            <div className="p-inputgroup">
              <Controller
                name="faks_region"
                render={({ field }) => (
                  <Dropdown
                    style={{ padding: 0, width: '5rem' }}
                    id="faks_region"
                    options={languages}
                    itemTemplate={countryOptionTemplate}
                    valueTemplate={selectedCountryTemplate}
                    data-cy="faks_region"
                    {...field}
                  />
                )}
              />
              <Controller
                name="faks"
                render={({ field }) => (
                  <InputText
                    name="faks"
                    style={{ width: '100%' }}
                    value={formatMobileNumber(
                      getValues()?.faks,
                      getValues()?.faks_region ||
                        getPhoneorMobileNumberRegion(getValues()?.faks)
                    )}
                    onChange={(e) => {
                      field.onChange(
                        e.target.value?.replace(
                          invalidPhoneNumberCharactersRegex,
                          ''
                        )
                      );
                    }}
                    //       onChange={(e) => {
                    //   setValue('mobilen', e.target.value);
                    // }}
                    className="data-cy-faks"
                  />
                )}
              />
            </div>
          </FieldWithErrors>

          <FieldWithErrors name="web" label={t('Website')}>
            <Controller
              name="web"
              render={({ field }) => (
                <InputText
                  id="web"
                  name="web"
                  value={field.value}
                  onChange={field.onChange}
                  max={255}
                />
              )}
            />
          </FieldWithErrors>

          <FieldWithErrors name="email" label={t('Email (username)')}>
            <Controller
              name="email"
              render={({ field }) => (
                <InputText
                  id="email"
                  name="email"
                  value={field.value}
                  onChange={field.onChange}
                  max={254}
                />
              )}
            />
          </FieldWithErrors>

          <FieldWithErrors name="odgovorno_lice" label={t('Person in charge')}>
            <Controller
              name="odgovorno_lice"
              render={({ field }) => (
                <InputText
                  id="odgovorno_lice"
                  name="odgovorno_lice"
                  value={field.value}
                  onChange={field.onChange}
                  max={45}
                />
              )}
            />
          </FieldWithErrors>

          <FieldWithErrors name="kontakt_lice" label={t('Contact person')}>
            <Controller
              name="kontakt_lice"
              render={({ field }) => (
                <InputText
                  id="kontakt_lice"
                  name="kontakt_lice"
                  value={field.value}
                  onChange={field.onChange}
                  max={45}
                />
              )}
            />
          </FieldWithErrors>
        </div>
      </div>
    </div>
  );
}

export default GeneralInfoTab;
